
import React, { useState } from 'react';
import './ResetareProgres.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotateLeft,
  faTriangleExclamation,
  faBookOpen,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';

const COURSES = [
  {
    id: 1,
    name: 'Bazele contabilitatii',
    description: 'Fundamentele contabilitatii pe intelesul tuturor.',
    icon: faBookOpen
  },
  {
    id: 2,
    name: 'Contabilitate financiara',
    description: 'Invata sa inregistrezi si sa intelegi operatiunile financiare.',
    icon: faCalculator
  }
];

const ResetareProgres = ({ onResetProgress }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const selectedCourseData = COURSES.find(
    (course) => course.id === selectedCourse
  );

  const handleOpen = () => {
    setSelectedCourse(null);
    setShowConfirm(true);
    setMessage('');
    setIsError(false);
  };

  const handleCancel = () => {
    if (isResetting) return;

    setShowConfirm(false);
    setSelectedCourse(null);
    setMessage('');
    setIsError(false);
  };

  const handleReset = async () => {
    if (!selectedCourseData || isResetting) return;

    if (typeof onResetProgress !== 'function') {
      setMessage('Functia de resetare nu este configurata.');
      setIsError(true);
      return;
    }

    const courseId = selectedCourseData.id;
    const courseName = selectedCourseData.name;

    try {
      setIsResetting(true);
      setMessage('');
      setIsError(false);

      // Trimite courseId catre functia din componenta parinte.
      // Aceasta trebuie sa apeleze backendul cu:
      // DELETE /user/reset-progress
      // Body: { courseId: 1 } sau { courseId: 2 }
      const result = await onResetProgress(courseId);

      // Daca functia parinte returneaza explicit un esec,
      // nu afisam mesajul de succes.
      if (
        result === false ||
        result?.success === false ||
        result?.ok === false
      ) {
        throw new Error('Resetarea nu a fost confirmata de server.');
      }

      setMessage(
        `Progresul pentru ${courseName} a fost resetat cu succes.`
      );

      setShowConfirm(false);
      setSelectedCourse(null);
    } catch (error) {
      console.error(
        'Eroare la resetarea progresului:',
        error
      );

      setMessage(
        'Progresul nu a putut fi resetat. Incearca din nou.'
      );

      setIsError(true);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="resetare-progres">
      <div className="resetare-progres__header">
        <div className="resetare-progres__icon">
          <FontAwesomeIcon icon={faRotateLeft} />
        </div>

        <div>
          <h4 className="resetare-progres__title">
            Resetare progres
          </h4>

          <p className="resetare-progres__description">
            Sterge progresul lectiilor, quiz-urilor si
            aplicatiilor finalizate pentru cursul selectat.
          </p>
        </div>
      </div>

      {!showConfirm && (
        <button
          type="button"
          className="resetare-progres__button"
          onClick={handleOpen}
          disabled={isResetting}
        >
          <FontAwesomeIcon icon={faRotateLeft} />
          <span>Reseteaza progresul</span>
        </button>
      )}

      {showConfirm && (
        <div className="resetare-progres__confirm">

          <p className="resetare-progres__select-title">
            Pentru ce curs doresti sa resetezi progresul?
          </p>

          <div className="resetare-progres__courses">
            {COURSES.map((course) => (
              <label
                key={course.id}
                className={`resetare-progres__course ${
                  selectedCourse === course.id
                    ? 'resetare-progres__course--selected'
                    : ''
                }`}
              >
                <input
                  type="radio"
                  name="resetare-progres-course"
                  value={course.id}
                  checked={selectedCourse === course.id}
                  onChange={() => {
                    setSelectedCourse(course.id);
                    setMessage('');
                    setIsError(false);
                  }}
                  disabled={isResetting}
                />

                <div className="resetare-progres__course-icon">
                  <FontAwesomeIcon icon={course.icon} />
                </div>

                <div className="resetare-progres__course-info">
                  <span className="resetare-progres__course-name">
                    {course.name}
                  </span>

                  <span className="resetare-progres__course-description">
                    {course.description}
                  </span>
                </div>
              </label>
            ))}
          </div>

          {selectedCourseData && (
            <div className="resetare-progres__warning">
              <FontAwesomeIcon icon={faTriangleExclamation} />

              <p>
                Esti sigur ca vrei sa resetezi progresul
                pentru{' '}
                <strong>{selectedCourseData.name}</strong>?
                {' '}Aceasta actiune nu poate fi anulata.
                Progresul celuilalt curs va ramane neschimbat
                daca resetarea este configurata corect pe server.
              </p>
            </div>
          )}

          <div className="resetare-progres__actions">
            <button
              type="button"
              className="resetare-progres__cancel"
              onClick={handleCancel}
              disabled={isResetting}
            >
              Anuleaza
            </button>

            <button
              type="button"
              className="resetare-progres__confirm-button"
              onClick={handleReset}
              disabled={!selectedCourseData || isResetting}
            >
              {isResetting
                ? 'Se reseteaza...'
                : 'Confirma resetarea'}
            </button>
          </div>
        </div>
      )}

      {message && (
        <p
          className={`resetare-progres__message ${
            isError
              ? 'resetare-progres__message--error'
              : 'resetare-progres__message--success'
          }`}
          role={isError ? 'alert' : 'status'}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default ResetareProgres;
